import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { Dialog, Box, Typography, Avatar } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { userHasAccessTokenSelector } from '@redux/oauth/oauthSelectors';
import { logoutThunk } from '@redux/oauth/oauthThunks';
import { Button } from 'components/Button/Button';
import { Color } from 'utils/color';
import { useAppDispatch, useAppSelector } from '../hooks';
import style from './SessionTimeoutProvider.scss';

// Set inactivity to 18 minutes and the warning countdown to 2 minutes.
export const INACTIVITY_DURATION_MS = 18 * 60 * 1000;
export const WARNING_DURATION_MS = 2 * 60 * 1000;

interface Props {
  children: React.ReactNode;
}

// Function to format milliseconds into MM:SS format
const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SessionTimeoutProvider: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(userHasAccessTokenSelector);
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WARNING_DURATION_MS);

  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const logoutTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownTimer = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = useCallback(() => {
    dispatch(logoutThunk());
  }, [dispatch]);

  const clearTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
  }, []);

  const startIdleTimer = useCallback(() => {
    clearTimers();
    if (!isLoggedIn) return;

    idleTimer.current = setTimeout(() => {
      setShowWarning(true);
      setTimeLeft(WARNING_DURATION_MS);

      // Start the visual countdown
      countdownTimer.current = setInterval(() => {
        setTimeLeft((prevTime) => (prevTime > 1000 ? prevTime - 1000 : 0));
      }, 1000);

      // Set the final logout timer
      logoutTimer.current = setTimeout(handleLogout, WARNING_DURATION_MS);
    }, INACTIVITY_DURATION_MS);
  }, [clearTimers, isLoggedIn, handleLogout]);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    clearTimers();
    startIdleTimer();
  }, [clearTimers, startIdleTimer]);

  useEffect(() => {
    if (isLoggedIn) {
      startIdleTimer();
      const events: (keyof WindowEventMap)[] = ['keydown', 'scroll', 'click'];
      const handler = () => resetTimer();

      events.forEach((event) => window.addEventListener(event, handler));

      return () => {
        events.forEach((event) => window.removeEventListener(event, handler));
        clearTimers();
      };
    }
  }, [isLoggedIn, resetTimer, startIdleTimer, clearTimers]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearTimers();
      setShowWarning(false);
    }
  }, [isLoggedIn, clearTimers]);

  return (
    <>
      {children}
      <Dialog
        open={showWarning}
        aria-labelledby="session-timeout-title"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '24px',
            minWidth: 420,
            fontFamily: 'Outfit',
            boxSizing: 'border-box'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Avatar sx={{ bgcolor: 'rgba(237, 108, 2, 0.1)', width: 48, height: 48, mb: 2 }}>
            <NotificationsOutlinedIcon sx={{ color: Color.ORANGE_600 }} />
          </Avatar>

          <Typography id="session-timeout-title" variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 1, fontFamily: 'Outfit', fontSize: '18px' }}>
            Your session is about to expire
          </Typography>

          <Typography sx={{ color: 'text.secondary', mb: 3, fontFamily: 'Outfit', fontSize: '16px' }}>
            For security purposes, you'll be logged out in 2 minutes due to inactivity.
          </Typography>

          <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', mb: 0.5, color: '#333', fontFamily: 'Outfit', alignSelf: 'center' }}>
            {formatTime(timeLeft)}
          </Typography>

          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 4, alignSelf: 'center' }}>
            Time remaining
          </Typography>

          <div className={style.buttonsWrapper}>
            <Button variant="outlined" onClick={handleLogout} className={style.button}>
              Log out now
            </Button>
            <Button variant="contained" onClick={resetTimer} className={style.button}>
              Continue
            </Button>
          </div>
        </Box>
      </Dialog>
    </>
  );
};

export default SessionTimeoutProvider;
